import { h, Component, Host, State, Element, Prop, Watch, Listen, Event, EventEmitter } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { ContextMenuSchemeDefinition, ContextMenuSchemeName, ContextMenuSize } from "./constants/context-menu-types";
import {
  CONTEXT_MENU_SCHEME_CONFIG,
  CONTEXT_MENU_SCHEME_MAP,
  CONTEXT_MENU_SIZE_NORMAL
} from "./constants/context-menu-constants";
import { ContextMenuSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { KeyIds } from "../../constants/keys";
import { isTouchDevice } from "../../utils/touch-helper";
import { setCssVariable } from "../../utils/css-variables";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-context-menu",
  styleUrl: "context-menu.scss",
  shadow: true
})
export class ContextMenu implements ColorableComponent<ContextMenuSchemeConfiguration<ContextMenuSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ContextMenuSchemeConfiguration<ContextMenuSchemeDefinition> = CONTEXT_MENU_SCHEME_CONFIG;
  schemeMap = CONTEXT_MENU_SCHEME_MAP;

  private menuContainerHead: HTMLElement;
  selectLabels: string[] = [];
  select: HTMLSelectElement;

  @State() selectItems: any[] = [];

  @State() containerHeight: any;

  @State() containerWidth: any;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ContextMenuSchemeDefinition;

  /**
   * Collection of all the menu items
   */
  @State() menuItems: HTMLEonUiContextMenuItemElement[] = [];

  /**
   * Newly Selected menu item
   */
  @State() selectedMenuItem: HTMLEonUiContextMenuItemElement;

  /**
   * Decides the side where the menu should be opened
   * @example "left" | "right"
   */
  @State() menuExpandPosition: string;

  /**
   * Newly Focused menu item
   */
  @State() focusedMenuItem: HTMLEonUiContextMenuItemElement;

  /**
   * Previously selected menu item
   */
  @State() prevSelectedMenuItem: HTMLEonUiContextMenuItemElement;

  /**
   * When Context Menu collapses
   */
  @State() collapseMenu: boolean;

  @Element() hostElement: HTMLEonUiContextMenuElement;

  /**
   * Raised when the input is focused.
   */
  @Event() contextMenuFocus: EventEmitter<FocusEvent>;

  /**
   * Raised when the input is losing focus.
   */
  @Event() contextMenuBlur: EventEmitter<FocusEvent>;

  /**
   * Raised when item has been selected from Context Menu.
   */
  @Event() contextMenuItemOnSelect: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ContextMenuSchemeName;

  /**
   * if this property is true, the context menu will be displayed
   */
  @Prop({ mutable: true, reflect: true }) visible: boolean = false;

  /**
   * Label Headline for Menu
   */
  @Prop({ reflect: true }) label: string;

  /**
   * Icon in aligned with label headline
   */
  @Prop({ reflect: true }) iconName: string;

  /**
   * Alignment on Icon on Menu title
   * @example "left", "right"
   */
  @Prop({ reflect: true, mutable: true }) iconAlignment: string = "right";

  /**
   * Allows to define an offset for exact positioning of context menu on x-axis
   */
  @Prop() menuOffsetX: number = 0;

  /**
   * Selected Value from context menun item component
   */
  @Prop({ reflect: true, mutable: true }) value: string;

  /**
   * Set the size of the component
   */
  @Prop() size: ContextMenuSize = CONTEXT_MENU_SIZE_NORMAL;

  /**
   * Icon and label are placed together
   */
  @Prop({ reflect: true, mutable: true }) iconWithLabel: boolean = false;

  componentWillLoad() {
    this.applyScheme();
    this.collectMenuItems();
    this.onWindowResize();
  }

  componentDidLoad() {
    detectSlotChanges(() => {
      this.collectMenuItems();
      this.menuElementSelectionHandler();
      this.onWindowResize();
    }, this.hostElement);

    this.setCssVariables();
    this.collectMenuItems();
    this.menuElementSelectionHandler();
    this.onWindowResize();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Listen("click", { target: "document" })
  onDocumentClick(event) {
    this.closeContextMenu(event);
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    this.initContextMenu();
    if (isTouchDevice()) this.initTouchContextMenu();
  }

  private initTouchContextMenu() {
    const eventFiredElement = this.hostElement.parentElement;
    this.containerWidth = eventFiredElement.getBoundingClientRect().width;
    this.containerHeight = eventFiredElement.getBoundingClientRect().height;
    let tempSelectItems: any[] = [];

    if (this.label) {
      let tempSelectLabelItem = {
        value: "",
        label: this.label,
        selectedItem: !this.selectedMenuItem ? true : false,
        innerText: this.label,
        disabled: true
      };

      tempSelectItems.push(tempSelectLabelItem);
    }

    this.menuItems.forEach((item: HTMLEonUiContextMenuItemElement) => {
      let optionElement = {
        value: item.value,
        label: item.label,
        href: item.href,
        selectedItem: this.selectedMenuItem && this.selectedMenuItem.value === item.value ? true : false,
        innerText: item.label
      };

      tempSelectItems.push(optionElement);
    });

    this.selectItems = tempSelectItems;
  }

  private initContextMenu() {
    const eventFiredElement = this.hostElement.parentElement;
    this.containerWidth = eventFiredElement.getBoundingClientRect().width;

    // Detects the position of parent from where the click event is fired
    // and on basis of that the menu should open on right or left
    let leftPos = eventFiredElement.getBoundingClientRect().left;
    this.menuExpandPosition = window.innerWidth / 2 < leftPos ? "right" : "left";

    if (this.menuExpandPosition === "left") {
      this.iconAlignment = "left";
    } else if (this.menuExpandPosition === "right") {
      this.iconAlignment = "right";
    }

    let offsetTop =
      this.hostElement.parentElement.getBoundingClientRect().height * 0.5 -
      this.menuContainerHead?.getBoundingClientRect().height * 0.5;

    setCssVariable(this.hostElement, `--offsetLeft`, `${this.menuOffsetX}px`);
    setCssVariable(this.hostElement, `--offsetTop`, `${offsetTop}px`);
  }

  @Watch("visible")
  private collectMenuItems() {
    this.menuItems = Array.from(
      getNestedChildrenOf(
        this.hostElement,
        "eon-ui-context-menu-item"
      ) as HTMLCollectionOf<HTMLEonUiContextMenuItemElement>
    );

    isTouchDevice() && this.contextMenuFocus.emit();
  }

  /* @Watch("visible")
  setFocus() {
    console.log("setFocus");
    if (this.visible) {
      console.log("visible");
      this.menuContainer.focus();
    } else {
      this.hostElement.parentElement.focus();
    }
  } */

  @Listen("contextMenuFocus")
  OpenContextMenu() {
    this.visible = true;
  }

  private closeContextMenu(e) {
    const srcElement = e.srcElement as HTMLElement;

    if (
      srcElement?.tagName != "EON-UI-CONTEXT-MENU" &&
      !this.hostElement.contains(srcElement) &&
      this.hostElement.parentElement !== srcElement
    ) {
      isTouchDevice() && e.stopImmediatePropagation();
      this.visible = false;
      this.toggleMenuList();
    }

    if (this.collapseMenu) {
      this.menuItems.forEach((menuItem: HTMLEonUiContextMenuItemElement) => {
        menuItem.focusedItem = null;
      });
    }
  }

  private menuElementSelectionHandler() {
    let prevSelectedMenuItem: HTMLEonUiContextMenuItemElement;

    this.menuItems.forEach((menuItem: HTMLEonUiContextMenuItemElement) => {
      menuItem.size = this.size;
      if (menuItem.selectedItem) {
        this.selectedMenuItem = menuItem;
        this.focusOnMenuItems(this.selectedMenuItem);
      }
      menuItem.onclick = (event) => {
        prevSelectedMenuItem = this.selectedMenuItem;
        this.selectedMenuItem = event.target as HTMLEonUiContextMenuItemElement;

        if (prevSelectedMenuItem?.label !== this.selectedMenuItem.label) {
          prevSelectedMenuItem && (prevSelectedMenuItem.selectedItem = null);
          this.selectedMenuItem.selectedItem = true;
          this.focusOnMenuItems(this.selectedMenuItem);
        }

        this.visible = false;
        this.toggleMenuList();
        this.value = this.selectedMenuItem?.value;
      };
    });
  }

  private toggleMenuList() {
    this.collapseMenu = !this.collapseMenu;
  }

  private handleMenuKeyDown(e: KeyboardEvent) {
    let focusedMenuItemIndex: number;

    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        if (this.collapseMenu) {
          if (this.selectedMenuItem) {
            this.focusedMenuItem = this.selectedMenuItem;
          }
          this.toggleMenuList();
        } else {
          if (this.focusedMenuItem) {
            this.selectionOfMenuItems(this.focusedMenuItem);
          }
          this.closeContextMenu(e);
        }
        break;
      case KeyIds.DOWN:
        e.preventDefault();
        if (this.collapseMenu) {
          if (this.selectedMenuItem) {
            this.focusedMenuItem = this.selectedMenuItem;
            this.focusOnMenuItems(this.focusedMenuItem);
          }
          this.toggleMenuList();
        } else {
          focusedMenuItemIndex = this.menuItems.indexOf(this.focusedMenuItem);
          if (focusedMenuItemIndex === -1) {
            this.focusOnMenuItems(this.menuItems[0]);
          } else if (focusedMenuItemIndex < this.menuItems.length - 1) {
            this.focusOnMenuItems(this.menuItems[focusedMenuItemIndex + 1]);
          } else if (focusedMenuItemIndex === this.menuItems.length - 1) {
            this.focusOnMenuItems(this.menuItems[0]);
          }
        }
        break;
      case KeyIds.UP:
        e.preventDefault();
        if (this.collapseMenu) {
          if (this.selectedMenuItem) {
            this.focusedMenuItem = this.selectedMenuItem;
            this.focusOnMenuItems(this.focusedMenuItem);
          }
          this.toggleMenuList();
        } else {
          focusedMenuItemIndex = this.menuItems.indexOf(this.focusedMenuItem);
          if (focusedMenuItemIndex === -1) {
            this.focusOnMenuItems(this.menuItems[0]);
          } else if (focusedMenuItemIndex > 0) {
            this.focusOnMenuItems(this.menuItems[focusedMenuItemIndex - 1]);
          } else if (focusedMenuItemIndex === 0) {
            this.focusOnMenuItems(this.menuItems[this.menuItems.length - 1]);
          }
        }
        break;
    }
  }

  private focusOnMenuItems(item: HTMLEonUiContextMenuItemElement) {
    this.focusedMenuItem = item;
    this.menuItems.forEach((menuItem: HTMLEonUiContextMenuItemElement) => {
      menuItem.focusedItem = null;
    });
    this.focusedMenuItem.focusedItem = true;
  }

  private selectionOfMenuItems(item: HTMLEonUiContextMenuItemElement) {
    this.selectedMenuItem = item;
    this.menuItems.forEach((menuItem: HTMLEonUiContextMenuItemElement) => {
      menuItem.selectedItem = null;
      this.value = this.selectedMenuItem?.value;
    });
    this.selectedMenuItem.selectedItem = true;
  }

  @Watch("selectedMenuItem")
  handleNewSelectedMenuItem() {
    this.contextMenuItemOnSelect.emit(this.selectedMenuItem);
  }

  private handleSelectionChange() {
    const newSelectedOption: HTMLEonUiContextMenuItemElement = this.menuItems.filter(
      (option) => option.value === this.select.value
    )[0];
    if (newSelectedOption) {
      this.menuItems.forEach((menuItem) => {
        menuItem.selectedItem = null;
      });
      newSelectedOption.selectedItem = true;
      this.selectedMenuItem = newSelectedOption;
      this.value = newSelectedOption.value;
    }
    if (this.selectedMenuItem.href) {
      window.location.href = this.selectedMenuItem.href;
    }
  }

  render() {
    const styleProp = this.containerHeight &&
      this.containerWidth && {
        style: {
          height: `${this.containerHeight}px`,
          width: `${this.containerWidth}px`
        }
      };
    return (
      <Host
        class={pfx({
          [`position-${this.menuExpandPosition}`]: !isTouchDevice()
        })}
      >
        {!isTouchDevice() && (
          <div
            role="listbox"
            tabIndex={0}
            class={pfx({
              "context-menu-container": true,
              ["visible"]: !!this.visible,
              [this.size]: this.size,
              [`position-${this.menuExpandPosition}`]: true
            })}
            onKeyDown={(event: KeyboardEvent) => this.handleMenuKeyDown(event)}
          >
            <div class={pfx({ "context-menu-wrapper": true })}>
              <div
                class={pfx({
                  "context-menu-title": true,
                  [`icon-${this.iconAlignment}`]: true,
                  "icon-with-label": !!this.iconWithLabel
                })}
                ref={(el) => (this.menuContainerHead = el as HTMLElement)}
              >
                {
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label class={pfx({ "context-menu-label": true })} innerHTML={this.label}></label>
                }
                {this.iconName && (
                  <eon-ui-icon
                    aria-label={this.iconName}
                    class={pfx({
                      "context-menu-icon": true
                    })}
                    size={this.size}
                    noSpace={true}
                    name={this.iconName}
                  ></eon-ui-icon>
                )}
              </div>
              <div class={pfx({ "context-menu-items": true })}>
                <slot></slot>
              </div>
            </div>
            <div class={pfx({ "divider-wrapper": true })}>
              <div class={pfx({ divider: true })}></div>
            </div>
          </div>
        )}
        {isTouchDevice() && (
          <div
            class={pfx({
              "context-menu-container": true,
              ["visible"]: !!this.visible,
              ["touch-device"]: true,
              [this.size]: this.size
            })}
            {...styleProp}
          >
            <select
              ref={(el) => (this.select = el as HTMLSelectElement)}
              onChange={() => this.handleSelectionChange()}
              onFocus={(event) => this.contextMenuFocus.emit(event)}
              onBlur={(event) => this.contextMenuBlur.emit(event)}
              name={this.value}
            >
              {this.selectItems.map((option) => (
                <option
                  value={option.value}
                  label={option.label}
                  selected={option.selectedItem}
                  disabled={option.disabled}
                >
                  {option.innerText}
                </option>
              ))}
            </select>
          </div>
        )}
      </Host>
    );
  }
}
